/**
 * AuditLogView — Read-only browser over every audit-log table in the tenant.
 *
 * Route: /#/admin/audit
 *
 * Tabs:
 *   - Activity log         (activity_log)
 *   - Push defense         (push_defense_audit_log)
 *   - Push re-entry        (push_reentry_audit_log)
 *   - Push retry           (push_retry_audit_log)
 *   - Soft-stop            (soft_stop_audit)
 *
 * Filters: date range + optional user + optional action substring.
 * Access: managers + executives only. Tenant-scoped via session.tenantId.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import { getSession, supabase } from "@/lib/session";

type TabKey = "activity" | "defense" | "reentry" | "retry" | "softstop";

const TABS: { key: TabKey; label: string; table: string; tsCol: string }[] = [
  { key: "activity",  label: "Activity",       table: "activity_log",             tsCol: "created_at" },
  { key: "defense",   label: "Push defense",   table: "push_defense_audit_log",   tsCol: "evaluated_at" },
  { key: "reentry",   label: "Push re-entry",  table: "push_reentry_audit_log",   tsCol: "occurred_at" },
  { key: "retry",     label: "Push retry",     table: "push_retry_audit_log",     tsCol: "created_at" },
  { key: "softstop",  label: "Soft-stop",      table: "soft_stop_audit",          tsCol: "timestamp" },
];

const PAGE_SIZE = 200;

function isoStartOfDay(d: string): string {
  return new Date(`${d}T00:00:00.000Z`).toISOString();
}
function isoEndOfDay(d: string): string {
  return new Date(`${d}T23:59:59.999Z`).toISOString();
}
function defaultRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
  const fmt = (x: Date) => x.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

export default function AuditLogView() {
  const session = getSession();
  const allowed = useMemo(() => {
    const r = session?.role || "";
    return r === "manager" || r === "executive";
  }, [session]);

  const [tab, setTab] = useState<TabKey>("activity");
  const range = useMemo(defaultRange, []);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [actionFilter, setActionFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (!session || !allowed) return;
    setLoading(true);
    setErr(null);
    setRows([]);
    try {
      const cfg = TABS.find(t => t.key === tab)!;
      const sb = supabase();
      let q = sb.from(cfg.table)
        .select("*")
        .gte(cfg.tsCol, isoStartOfDay(from))
        .lte(cfg.tsCol, isoEndOfDay(to))
        .order(cfg.tsCol, { ascending: false })
        .limit(PAGE_SIZE);
      // Tenant scoping — every table has tenant_id except push_retry_audit_log
      // which is joined through account_id. We still filter where the column exists.
      if (cfg.table !== "push_retry_audit_log" && cfg.table !== "push_reentry_audit_log") {
        q = q.eq("tenant_id", session.tenantId);
      }
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      let filtered = (data || []) as any[];
      if (actionFilter.trim()) {
        const needle = actionFilter.trim().toLowerCase();
        filtered = filtered.filter(r => {
          const hay = JSON.stringify(r).toLowerCase();
          return hay.includes(needle);
        });
      }
      if (userFilter.trim()) {
        const u = userFilter.trim().toLowerCase();
        filtered = filtered.filter(r => {
          const candidates = [r.user_id, r.actor_id, r.actor_name, r.rep_user_id]
            .filter(Boolean).map(String).map(s => s.toLowerCase());
          return candidates.some(s => s.includes(u));
        });
      }
      setRows(filtered);
    } catch (e: any) {
      setErr(e?.message || "Failed to load audit rows.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const breadcrumbs = [
    { label: "Home", href: "/" },
    { label: "Admin" },
    { label: "Audit log" },
  ];

  return (
    <OperatorShell
      breadcrumbs={breadcrumbs}
      pageTitle="Audit Log"
      pageMeta="Read-only history of every recorded event"
    >
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1200px)", gap: 16 }}>
        {!allowed && (
          <Card title="Restricted">
            <p style={hintStyle}>Only managers and executives can view the audit log.</p>
          </Card>
        )}

        {allowed && (
          <>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", padding: 4, background: "#F3F4F6", borderRadius: 8 }}>
              {TABS.map(t => {
                const active = t.key === tab;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "none",
                      background: active ? "#FFFFFF" : "transparent",
                      color: active ? tokens.color.black : tokens.color.textMuted,
                      boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Filters */}
            <Card title="Filters">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
                <Field label="From">
                  <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
                </Field>
                <Field label="To">
                  <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Search text">
                  <input type="text" value={actionFilter} onChange={e => setActionFilter(e.target.value)} placeholder="e.g. push_retry"  style={inputStyle} />
                </Field>
                <Field label="User ID or name">
                  <input type="text" value={userFilter} onChange={e => setUserFilter(e.target.value)} placeholder="UUID or partial name"  style={inputStyle} />
                </Field>
                <button type="button" onClick={load} style={primaryBtn}>
                  {loading ? "Loading…" : "Apply"}
                </button>
              </div>
            </Card>

            {/* Table */}
            <Card title={`${TABS.find(t => t.key === tab)?.label} entries${rows.length ? ` — ${rows.length}` : ""}`}>
              {err && <InlineMsg kind="err">{err}</InlineMsg>}
              {!err && rows.length === 0 && !loading && (
                <p style={hintStyle}>No records in this range.</p>
              )}
              {!err && rows.length > 0 && (
                <div style={{ overflowX: "auto", border: `1px solid ${tokens.color.border}`, borderRadius: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#F9FAFB", borderBottom: `1px solid ${tokens.color.border}` }}>
                        <th style={th}>When</th>
                        <th style={th}>Summary</th>
                        <th style={th}>Actor</th>
                        <th style={th}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${tokens.color.border}` }}>
                          <td style={td}>{formatWhen(r, tab)}</td>
                          <td style={td}>{formatSummary(r, tab)}</td>
                          <td style={td}>{formatActor(r)}</td>
                          <td style={tdMono}>{formatDetail(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </OperatorShell>
  );
}

/* ---------- Row formatters ---------- */

function formatWhen(r: any, tab: TabKey): string {
  const cfg = TABS.find(t => t.key === tab)!;
  const v = r[cfg.tsCol];
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function formatSummary(r: any, tab: TabKey): string {
  switch (tab) {
    case "activity":
      return [r.entity_type, r.action, r.channel, r.outcome_notes].filter(Boolean).join(" · ") || "—";
    case "defense":
      return `Gate ${r.gate_result || "—"} · deal ${r.deal_id || "—"}`;
    case "reentry":
      return `${r.event_type || "event"} · CP${r.cp_number ?? "—"} · deal ${r.deal_id || "—"}`;
    case "retry":
      return `${r.event_type || "event"} · ${r.trigger_reason || "—"} · acct ${r.account_id || "—"}`;
    case "softstop":
      return `${r.event_type || "event"} · CP${r.cp_number ?? "—"} · ${r.question_key || "—"}`;
  }
}

function formatActor(r: any): string {
  if (r.actor_name) return `${r.actor_name}${r.actor_role ? ` (${r.actor_role})` : ""}`;
  if (r.user_id) return short(r.user_id);
  if (r.actor_id) return short(r.actor_id);
  if (r.rep_user_id) return short(r.rep_user_id);
  return "—";
}

function formatDetail(r: any): string {
  const blob: Record<string, any> = {};
  for (const k of ["payload", "gate_detail", "detail", "details", "prior_soft_stop_context", "decision", "reason"]) {
    if (r[k] !== undefined && r[k] !== null) blob[k] = r[k];
  }
  const s = JSON.stringify(blob);
  return s.length > 220 ? s.slice(0, 220) + "…" : s;
}

function short(id: any): string {
  const s = String(id);
  return s.length > 8 ? s.slice(0, 8) : s;
}

/* ---------- UI primitives ---------- */

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{
      background: "#FFFFFF",
      border: `1px solid ${tokens.color.border}`,
      borderRadius: tokens.radius.card,
      padding: 20,
    }}>
      <h2 style={{ margin: "0 0 12px", fontFamily: tokens.font.display, fontSize: 16, fontWeight: 700 }}>
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function InlineMsg({ kind, children }: { kind: "ok" | "err"; children: React.ReactNode }) {
  const isOk = kind === "ok";
  return (
    <span style={{
      fontSize: 12,
      color: isOk ? tokens.color.success : tokens.color.danger,
      background: isOk ? tokens.color.successBg : tokens.color.dangerBg,
      padding: "4px 10px",
      borderRadius: 4,
    }}>{children}</span>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: `1px solid ${tokens.color.border}`,
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "inherit",
  background: "#FFFFFF",
  color: tokens.color.textPrimary,
};

const primaryBtn: React.CSSProperties = {
  background: tokens.color.black,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 6,
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: tokens.color.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const td: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 12,
  color: tokens.color.textPrimary,
  verticalAlign: "top",
};

const tdMono: React.CSSProperties = {
  ...td,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxWidth: 480,
};

const hintStyle: React.CSSProperties = {
  margin: "4px 0 0",
  fontSize: 12,
  color: tokens.color.textMuted,
};
